import { useEffect, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Mail, CheckCircle2, XCircle, Info, RefreshCw, Search, Server, Radio } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import usePagination from "@/hooks/usePagination";

const STATUS_META = {
  sent:    { label: "Sent",    tone: "bg-green-50 text-green-700 border-green-300", icon: CheckCircle2 },
  failed:  { label: "Failed",  tone: "bg-red-50 text-primary border-primary",       icon: XCircle },
  skipped: { label: "Skipped", tone: "bg-slate-50 text-slate-500 border-slate-300", icon: Info },
};

const CATEGORY_LABELS = {
  "parcel.arrival":   "Parcel arrival",
  "priority.alert":   "Priority alert",
  "pickup.request":   "Pickup request",
  "pickup.quick":     "Quick pickup",
  "branch.transfer":  "Branch transfer",
  "user.welcome":     "User welcome",
  "reports.email":    "Reports export",
  "smtp.test":        "SMTP test",
  "general":          "General",
};

const rowStatus = (r) => (r.via === "skipped" ? "skipped" : r.ok ? "sent" : "failed");

export default function EmailLogsPage() {
  const [data, setData] = useState({ items: [], summary: {}, categories: [], days: 30 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ days: 30, status: "", category: "", to: "" });
  const { pageItems, Pagination } = usePagination(data.items || []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const { data } = await api.get(`/email-logs?${params.toString()}`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.days, filters.status, filters.category]);

  const summary = data.summary || {};

  return (
    <PageShell
      title="Mail logs."
      overline={<><Mail size={12} /> Outbound email · Delivery log</>}
      subtitle="Every notification we send — parcel arrivals, priority alerts, pickup requests, user welcomes, SMTP tests, report exports — is recorded here with delivery status, latency, and any error returned by your relay."
      testid="email-logs"
      actions={
        <Button data-testid="email-logs-refresh" onClick={load} disabled={loading} className="rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
          <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> {loading ? "Loading" : "Refresh"}
        </Button>
      }
    >

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Total" value={summary.total ?? 0} testid="kpi-total" />
        <Kpi label="Delivered" value={summary.sent ?? 0} tone="green" testid="kpi-sent" />
        <Kpi label="Failed" value={summary.failed ?? 0} tone="red" testid="kpi-failed" />
        <Kpi label="Delivery rate" value={`${summary.delivery_rate ?? 0}%`} tone="mono" testid="kpi-rate" />
      </div>

      {/* Categories */}
      {data.categories?.length > 0 && (
        <div className="border border-border bg-card rounded-md p-4">
          <div className="overline mb-3 flex items-center gap-2"><Radio size={12} /> By category · last {data.days} days</div>
          <div className="flex flex-wrap gap-2">
            {data.categories.map((c) => (
              <button key={c.category} onClick={() => setFilters({ ...filters, category: c.category === filters.category ? "" : c.category })}
                data-testid={`chip-cat-${c.category}`}
                className={`text-xs px-2.5 py-1 rounded border transition-colors ${filters.category === c.category
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border hover:border-primary hover:text-primary"}`}>
                {CATEGORY_LABELS[c.category] || c.category} · <span className="font-mono">{c.count}</span>
                {c.failed > 0 && <span className="ml-1 text-primary font-bold">({c.failed}⚠)</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="border border-border bg-card rounded-md p-4 flex flex-wrap items-end gap-3">
        <div>
          <div className="overline mb-1">Window</div>
          <Select value={String(filters.days)} onValueChange={(v) => setFilters({ ...filters, days: parseInt(v, 10) })}>
            <SelectTrigger data-testid="filter-days" className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24h</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="overline mb-1">Status</div>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-status" className="w-40"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="sent">Delivered</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="skipped">Skipped</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="overline mb-1">Search recipient</div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="filter-to" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="e.g. @acme.io" className="pl-8" />
          </div>
        </div>
        <Button data-testid="filter-apply" onClick={load} variant="outline" className="rounded-md">Apply</Button>
        {(filters.status || filters.category || filters.to) && (
          <Button data-testid="filter-clear" onClick={() => setFilters({ days: filters.days, status: "", category: "", to: "" })} variant="outline" className="rounded-md">Clear</Button>
        )}
      </div>

      {/* Table */}
      <div className="border border-border bg-card rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left overline">
              <th className="p-4">When</th>
              <th className="p-4">Status</th>
              <th className="p-4">Category</th>
              <th className="p-4">To</th>
              <th className="p-4">Subject</th>
              <th className="p-4">Via</th>
              <th className="p-4">Error</th>
            </tr>
          </thead>
          <tbody data-testid="email-logs-table">
            {data.items?.length === 0 && !loading && (
              <tr><td colSpan={7} className="p-10 text-center text-sm text-muted-foreground">
                No email activity in the selected window.
              </td></tr>
            )}
            {pageItems.map((r) => {
              const s = rowStatus(r);
              const meta = STATUS_META[s];
              const Icon = meta.icon;
              return (
                <tr key={r.id} className="border-b border-border hover:bg-muted/40" data-testid={`log-row-${r.id}`}>
                  <td className="p-4 font-mono text-[11px] whitespace-nowrap">{r.created_at?.slice(0, 19).replace("T", " ")}</td>
                  <td className="p-4">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded font-semibold ${meta.tone}`}>
                      <Icon size={10} /> {meta.label}
                    </span>
                  </td>
                  <td className="p-4 text-xs">
                    <span className="mono">{CATEGORY_LABELS[r.category] || r.category}</span>
                  </td>
                  <td className="p-4 font-mono text-xs">{r.to || "—"}</td>
                  <td className="p-4 text-xs truncate max-w-[300px]">{r.subject}</td>
                  <td className="p-4">
                    <span className="inline-flex items-center gap-1 text-[10px] mono uppercase tracking-widest border border-border px-1.5 py-0.5 rounded">
                      <Server size={10} /> {r.via || "—"}
                    </span>
                  </td>
                  <td className="p-4 text-[11px] text-primary font-mono truncate max-w-[260px]" title={r.error || ""}>
                    {r.error || ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination />
    </PageShell>
  );
}

function Kpi({ label, value, tone, testid }) {
  const toneClass = tone === "green" ? "text-green-700 border-green-300 bg-green-50"
    : tone === "red" ? "text-primary border-primary bg-red-50"
    : tone === "mono" ? "text-foreground border-border bg-card mono"
    : "text-foreground border-border bg-card";
  return (
    <div className={`border rounded-md p-4 ${toneClass}`} data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="text-3xl font-display mt-1">{value}</div>
    </div>
  );
}
